import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Chip,
  LinearProgress,
  Alert,
} from "@mui/material";
import { adminTheme } from "theme/adminTheme";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SendIcon from "@mui/icons-material/Send";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import { StepCreateRequest } from "api/main/data-contracts";
import { createApi } from "util/ApiCreator";
import { Llm } from "api/main/Llm";

interface AIProcessGeneratorProps {
  onGenerate: (name: string, steps: StepCreateRequest[]) => void;
  onCancel: () => void;
  availableStepTypes: string[];
}

// Step types that should never be generated by AI
const BLOCKED_STEP_TYPES = ["PAYMENT", "NOTIFICATION", "DATA_OUTPUT"];

interface GeneratedProcess {
  name: string;
  steps: StepCreateRequest[];
}

const examplePrompts = [
  "Sukurti duomenų rinkimo procesą su mokėjimais",
  "Noriu proceso su dokumentų pasirašymu",
  "Paprasta forma su patvirtinimu",
  "Mokėjimo forma su pranešimu",
  "Registracijos forma su asmens kodo ir telefono validacija",
  "Sutarties pasirašymo procesas su identifikacija",
];

export default function AIProcessGenerator({
  onGenerate,
  onCancel,
  availableStepTypes,
}: AIProcessGeneratorProps) {
  const [prompt, setPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedSteps, setGeneratedSteps] = useState<StepCreateRequest[] | null>(null);
  const [generatedName, setGeneratedName] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  console.log("[AIProcessGenerator] Component mounted, availableStepTypes:", availableStepTypes);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      console.log("[AIProcessGenerator] Empty prompt, returning");
      return;
    }

    console.log("[AIProcessGenerator] Starting generation with prompt:", prompt);
    setIsGenerating(true);
    setGeneratedSteps(null);
    setError(null);

    try {
      console.log("[AIProcessGenerator] Creating LLM API client...");
      const llmApi: Llm = createApi("llm");

      console.log("[AIProcessGenerator] Sending request to backend...");
      const response = await llmApi.processText({ text: prompt });

      console.log("[AIProcessGenerator] Received response:", response);
      console.log("[AIProcessGenerator] JSON result:", response.jsonResult);

      if (response.jsonResult) {
        console.log("[AIProcessGenerator] Parsing JSON result...");
        console.log("[AIProcessGenerator] Raw JSON:", response.jsonResult);
        const parsed: GeneratedProcess = JSON.parse(response.jsonResult);
        console.log("[AIProcessGenerator] Parsed process:", parsed);

        // Log inputContent with validations for each step
        parsed.steps.forEach((step, idx) => {
          console.log(`[AIProcessGenerator] Step ${idx} type:`, step.type);
          console.log(`[AIProcessGenerator] Step ${idx} stepInformation:`, JSON.stringify(step.stepInformation, null, 2));
          if (step.stepInformation?.inputContent) {
            step.stepInformation.inputContent.forEach((group, gIdx) => {
              group.forEach((field, fIdx) => {
                console.log(`[AIProcessGenerator] Step ${idx} Group ${gIdx} Field ${fIdx}:`, field.name, "validations:", field.validations);
              });
            });
          }
        });

        // Filter steps to only include available step types and exclude blocked types
        const filteredSteps = parsed.steps.filter((step: StepCreateRequest) => {
          const stepType = step.type || "";
          const isAvailable = availableStepTypes.includes(stepType);
          const isBlocked = BLOCKED_STEP_TYPES.includes(stepType);
          console.log(`[AIProcessGenerator] Step type ${stepType} available: ${isAvailable}, blocked: ${isBlocked}`);
          return isAvailable && !isBlocked;
        });

        console.log("[AIProcessGenerator] Filtered steps:", filteredSteps);

        // Valid stepInformation fields according to backend
        const validStepInfoFields = [
          "stripePaymentContent",
          "paymentContent",
          "generateDocumentContent",
          "signContent",
          "uploadDocumentContent",
          "inputContent",
        ];

        // Fields that should be set to null (not needed for step types without configuration)
        const fieldsToExclude = ["confirmationContent", "outputContent", "notificationContent"];

        // Ensure each step has required fields and filter out unknown stepInformation fields
        const processedSteps = filteredSteps.map((step: StepCreateRequest) => {
          // Filter stepInformation to only include valid fields
          const cleanedStepInfo: Record<string, any> = {};
          if (step.stepInformation) {
            for (const key of validStepInfoFields) {
              if ((step.stepInformation as any)[key] !== undefined && !fieldsToExclude.includes(key)) {
                cleanedStepInfo[key] = (step.stepInformation as any)[key];
              }
            }
          }

          return {
            ...step,
            i18nkey: step.i18nkey || "",
            executors: step.executors || [],
            notificationType: step.notificationType || "NONE",
            stepInformation: Object.keys(cleanedStepInfo).length > 0 ? cleanedStepInfo : undefined,
          };
        });

        console.log("[AIProcessGenerator] Processed steps:", processedSteps);

        // Log processed steps with validations
        processedSteps.forEach((step, idx) => {
          console.log(`[AIProcessGenerator] Processed Step ${idx} stepInformation:`, JSON.stringify(step.stepInformation, null, 2));
          if (step.stepInformation?.inputContent) {
            (step.stepInformation.inputContent as any[][]).forEach((group: any[], gIdx: number) => {
              group.forEach((field: any, fIdx: number) => {
                console.log(`[AIProcessGenerator] Processed Step ${idx} Group ${gIdx} Field ${fIdx}:`, field.name, "validations:", field.validations);
              });
            });
          }
        });

        setGeneratedName(parsed.name || "AI sugeneruotas procesas");
        setGeneratedSteps(processedSteps);
        console.log("[AIProcessGenerator] Generation successful!");
      } else {
        console.error("[AIProcessGenerator] No jsonResult in response");
        setError("AI negrąžino rezultato. Bandykite dar kartą.");
      }
    } catch (err: any) {
      console.error("[AIProcessGenerator] Error generating process:", err);
      console.error("[AIProcessGenerator] Error response:", err?.response);
      console.error("[AIProcessGenerator] Error data:", err?.response?.data);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Įvyko klaida generuojant procesą. Bandykite dar kartą."
      );
    } finally {
      setIsGenerating(false);
      console.log("[AIProcessGenerator] Generation finished");
    }
  };

  const handleConfirm = () => {
    if (generatedSteps && generatedSteps.length > 0) {
      console.log("[AIProcessGenerator] Confirming generation with name:", generatedName, "steps:", generatedSteps);
      onGenerate(generatedName, generatedSteps);
    }
  };

  const handleExampleClick = (example: string) => {
    console.log("[AIProcessGenerator] Example clicked:", example);
    setPrompt(example);
    setGeneratedSteps(null);
    setError(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: adminTheme.borderRadius.md,
            background: `linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AutoAwesomeIcon sx={{ color: "white", fontSize: 24 }} />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            AI proceso generatorius
          </Typography>
          <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted }}>
            Aprašykite, kokį procesą norite sukurti
          </Typography>
        </Box>
      </Box>

      {/* Input */}
      <TextField
        fullWidth
        multiline
        rows={3}
        placeholder="Pvz.: Noriu sukurti duomenų rinkimo procesą su mokėjimo žingsniu ir pranešimu pabaigoje..."
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": {
            borderRadius: adminTheme.borderRadius.md,
          },
        }}
      />

      {/* Example prompts */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <LightbulbIcon sx={{ fontSize: 16, color: adminTheme.colors.warning }} />
          <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted }}>
            Pasirinkite pavyzdį:
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {examplePrompts.map((example, index) => (
            <Chip
              key={index}
              label={example}
              size="small"
              onClick={() => handleExampleClick(example)}
              sx={{
                cursor: "pointer",
                bgcolor: adminTheme.colors.backgroundAlt,
                "&:hover": {
                  bgcolor: `${adminTheme.colors.primary}15`,
                  color: adminTheme.colors.primary,
                },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Generate button */}
      <Button
        variant="contained"
        fullWidth
        onClick={handleGenerate}
        disabled={!prompt.trim() || isGenerating}
        startIcon={isGenerating ? null : <AutoAwesomeIcon />}
        sx={{
          mb: 3,
          py: 1.5,
          background: `linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)`,
          borderRadius: adminTheme.borderRadius.md,
          fontWeight: 600,
          "&:hover": {
            background: `linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)`,
          },
        }}
      >
        {isGenerating ? "Generuojama..." : "Generuoti procesą"}
      </Button>

      {/* Loading */}
      {isGenerating && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress
            sx={{
              borderRadius: 4,
              bgcolor: `${adminTheme.colors.primary}20`,
              "& .MuiLinearProgress-bar": {
                background: `linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)`,
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{ color: adminTheme.colors.textMuted, display: "block", mt: 1, textAlign: "center" }}
          >
            AI analizuoja jūsų užklausimą...
          </Typography>
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: adminTheme.borderRadius.sm }}>
          {error}
        </Alert>
      )}

      {/* Results */}
      {generatedSteps && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            bgcolor: adminTheme.colors.backgroundAlt,
            border: `1px solid ${adminTheme.colors.border}`,
            borderRadius: adminTheme.borderRadius.md,
            mb: 3,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
            Sugeneruotas procesas: "{generatedName}"
          </Typography>
          <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted, display: "block", mb: 2 }}>
            {generatedSteps.length} žingsnis(-iai) sugeneruoti
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {generatedSteps.map((step, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 1.5,
                  bgcolor: adminTheme.colors.background,
                  borderRadius: adminTheme.borderRadius.sm,
                  border: `1px solid ${adminTheme.colors.border}`,
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    bgcolor: adminTheme.colors.primary,
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                  }}
                >
                  {index + 1}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {step.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: adminTheme.colors.textMuted }}>
                    {step.type}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Alert
            severity="info"
            sx={{ mt: 2, borderRadius: adminTheme.borderRadius.sm }}
          >
            Galite redaguoti žingsnius po sukūrimo
          </Alert>
        </Paper>
      )}

      {/* Action buttons */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          sx={{
            flex: 1,
            borderColor: adminTheme.colors.border,
            color: adminTheme.colors.textSecondary,
            borderRadius: adminTheme.borderRadius.md,
          }}
        >
          Atsaukti
        </Button>
        {generatedSteps && generatedSteps.length > 0 && (
          <Button
            variant="contained"
            onClick={handleConfirm}
            startIcon={<SendIcon />}
            sx={{
              flex: 1,
              background: adminTheme.gradients.primary,
              borderRadius: adminTheme.borderRadius.md,
              fontWeight: 600,
            }}
          >
            Naudoti šį procesą
          </Button>
        )}
      </Box>
    </Box>
  );
}
